import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Package,
  Users,
  FolderOpen,
  AlertTriangle,
  Clock,
  CheckCircle,
  Building2,
  FolderKanban,
  AppWindow,
  Inbox,
} from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { PageHeader } from '@/components/ui/page-header'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api-client'
import { getRelativeTime } from '@shared/utils'

interface DashboardStats {
  totalDevices: number
  availableDevices: number
  assignedDevices: number
  devicesUnderRepair: number
  companyAssets: number
  projectAssets: number
  totalUsers: number
  totalProjects: number
  totalModels: number
  totalSoftware: number
  totalSoftwareAccounts: number
  pendingAcceptance: any[]
  pendingRequests: any[]
  recentAssignments: any[]
}

export function AdminDashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      const [devices, users, projects, assignments, pendingAcceptance, pendingRequests, models, software] = await Promise.all([
        apiClient.get('/devices', { params: { limit: 1000 } }),
        apiClient.get('/users'),
        apiClient.get('/projects'),
        apiClient.get('/assignments'),
        apiClient.get('/assignments', { params: { status: 'pending' } }),
        apiClient.get('/device-requests', { params: { status: 'pending' } }),
        apiClient.get('/device-models'),
        apiClient.get('/software', { params: { limit: 1 } }),
      ])

      const list = devices.data.devices || []
      const deviceStats = list.reduce((acc: any, device: any) => {
        acc.total += 1
        if (device.status === 'available') acc.available += 1
        else if (device.status === 'assigned') acc.assigned += 1
        else if (device.status === 'broken' || device.status === 'under-repair') acc.underRepair += 1
        if (device.ownershipType === 'project') acc.project += 1
        else acc.company += 1
        return acc
      }, { total: 0, available: 0, assigned: 0, underRepair: 0, company: 0, project: 0 })

      return {
        totalDevices: deviceStats.total,
        availableDevices: deviceStats.available,
        assignedDevices: deviceStats.assigned,
        devicesUnderRepair: deviceStats.underRepair,
        companyAssets: deviceStats.company,
        projectAssets: deviceStats.project,
        totalUsers: users.data.length,
        totalProjects: projects.data.length,
        totalModels: models.data.length,
        totalSoftware: software.data.pagination?.totalItems || 0,
        totalSoftwareAccounts: software.data.pagination?.totalAccounts || 0,
        pendingAcceptance: pendingAcceptance.data || [],
        pendingRequests: pendingRequests.data || [],
        recentAssignments: (assignments.data || []).slice(0, 5),
      }
    },
  })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const cards = [
    {
      title: 'Pending acceptance',
      value: stats?.pendingAcceptance.length || 0,
      icon: Clock,
      href: '/devices?status=assigned_pending',
      detail: 'Assets handed over that the employee has not accepted yet.',
      alert: true,
    },
    {
      title: 'Requests to review',
      value: stats?.pendingRequests.length || 0,
      icon: Inbox,
      href: '/requests',
      detail: 'Open requests for a new device, a replacement, or a damage report.',
      alert: true,
    },
    {
      title: 'Catalog models',
      value: stats?.totalModels || 0,
      icon: Package,
      href: '/devices',
      detail: 'Device models in the catalog. Opens the full device list.',
    },
    {
      title: 'Total assets',
      value: stats?.totalDevices || 0,
      icon: Package,
      href: '/devices',
      detail: 'Every asset, including available, assigned, and out of service.',
    },
    {
      title: 'Available',
      value: stats?.availableDevices || 0,
      icon: CheckCircle,
      href: '/devices?status=available',
      detail: 'Assets that are free to assign.',
    },
    {
      title: 'Assigned',
      value: stats?.assignedDevices || 0,
      icon: Users,
      href: '/devices?status=assigned',
      detail: 'Assets the employee has already accepted.',
    },
    {
      title: 'Company assets',
      value: stats?.companyAssets || 0,
      icon: Building2,
      href: '/devices?ownership=company',
      detail: 'Assets owned by the company, not tied to a project.',
    },
    {
      title: 'Project assets',
      value: stats?.projectAssets || 0,
      icon: FolderKanban,
      href: '/devices?ownership=project',
      detail: 'Assets owned by a project.',
    },
    {
      title: 'Needs attention',
      value: stats?.devicesUnderRepair || 0,
      icon: AlertTriangle,
      href: '/devices?status=broken',
      detail: 'Broken assets. Completely used and end of life are counted separately.',
    },
    {
      title: 'Paid software',
      value: stats?.totalSoftware || 0,
      icon: AppWindow,
      href: '/software',
      detail: 'Software products with paid accounts.',
    },
    {
      title: 'Paid accounts',
      value: stats?.totalSoftwareAccounts || 0,
      icon: AppWindow,
      href: '/software',
      detail: 'Paid logins across all software. Each product lists its accounts.',
    },
    {
      title: 'Projects',
      value: stats?.totalProjects || 0,
      icon: FolderOpen,
      href: '/projects',
      detail: 'Projects that can own assets.',
    },
  ]

  const acceptance = (stats?.pendingAcceptance || []).slice(0, 8)
  const requests = (stats?.pendingRequests || []).slice(0, 8)

  return (
    <div className="space-y-4">
      <PageHeader title="Dashboard" />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {cards.map((card) => (
          <Link
            key={card.title}
            to={card.href}
            title={card.detail}
            className={`group relative block rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/40 ${card.alert && card.value > 0 ? 'border-primary' : 'border-border'}`}
          >
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{card.title}</p>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums">{card.value}</p>
            <p className="pointer-events-none absolute inset-x-2 top-full z-30 mt-1 hidden rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground shadow-md group-hover:block group-focus-visible:block">
              {card.detail}
            </p>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="data-panel">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Waiting for acceptance</p>
                <p className="text-xs text-muted-foreground">Assigned devices the person has not accepted yet</p>
              </div>
            </div>
            <Badge variant={acceptance.length ? 'warning' : 'outline'}>{stats?.pendingAcceptance.length || 0}</Badge>
          </div>
          {acceptance.length ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Employee</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {acceptance.map((assignment: any) => (
                  <tr key={assignment.id}>
                    <td>
                      <Link to={`/devices/${assignment.device?.id}`} className="font-medium text-primary hover:underline">
                        {assignment.device?.serialNumber || assignment.device?.name || '—'}
                      </Link>
                    </td>
                    <td data-label="Employee">{assignment.employee?.name || '—'}</td>
                    <td data-label="When" className="text-muted-foreground">{getRelativeTime(assignment.assignedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-4 py-8 text-sm text-muted-foreground">No devices are waiting to be accepted.</p>
          )}
        </div>

        <div className="data-panel">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Requests to review</p>
                <p className="text-xs text-muted-foreground">New, replacement, and damage requests</p>
              </div>
            </div>
            <Link to="/requests" className="text-xs font-medium text-primary hover:underline">Open queue</Link>
          </div>
          {requests.length ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Needed</th>
                  <th>From</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request: any) => (
                  <tr key={request.id}>
                    <td className="font-medium">{request.deviceName}</td>
                    <td data-label="From">{request.requester?.name || '—'}</td>
                    <td data-label="Type" className="text-muted-foreground">
                      {request.requestType === 'damage' ? 'Damage' : request.requestType === 'replace' ? 'Replace' : 'New'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-4 py-8 text-sm text-muted-foreground">No open device requests.</p>
          )}
        </div>
      </div>

      <div className="data-panel">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Recent assignments</p>
            <p className="text-xs text-muted-foreground">Latest device handovers</p>
          </div>
        </div>
        {stats?.recentAssignments?.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Employee</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentAssignments.map((assignment: any) => (
                <tr key={assignment.id}>
                  <td className="font-medium">{assignment.device?.serialNumber || assignment.device?.name || '—'}</td>
                  <td data-label="Employee">{assignment.employee?.name || '—'}</td>
                  <td data-label="When" className="text-muted-foreground">{getRelativeTime(assignment.assignedOn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-8 text-sm text-muted-foreground">No recent assignments</p>
        )}
      </div>
    </div>
  )
}
